import React, { useState, useEffect } from 'react';
import 'App.css';
import { Box } from '@mui/material';

// Components
import { BrowserRouter, Route, useRouteMatch, useLocation, Switch } from 'react-router-dom';
import PrivateRoute from 'components/PrivateRoute';
import RootAppBar from 'components/navigation/RootAppBar';
import RootTabs from 'components/navigation/RootTabs';
import AdminMenu from 'components/admin/AdminMenu';

// Utility
import { useAuth } from 'context/auth';
import ReactGA from 'react-ga';

// Pages
import Login from 'pages/Login';
import HostSelect from 'pages/HostSelect';
import HostRedirect from 'pages/HostRedirect';
import Home from 'pages/Home';
import EventList from 'pages/eventList/EventList';
import Settings from 'pages/Settings';
import StripeOnboarding from 'pages/stripeOnboarding/StripeOnboarding';
import Notification from 'components/Notification';
import ForgotPassword from 'pages/ForgotPassword';
import ResetPassword from 'pages/ResetPassword';
import PageNotFound from 'pages/PageNotFound';

// Navigators
import EventNavigator from 'components/navigation/EventNavigator';
import AdminNavigator from 'components/navigation/AdminNavigator';
import McpAuthorize from 'pages/admin/mcp/McpAuthorize';

export default function RootNavigator() {
    // Initialize ReactGA (Google Analytics)
    const [ready, setReady] = useState(false);

    useEffect(() => {
        // Enable Google Analytics in Production
        if (process.env.REACT_APP_ENV === 'production') {
            ReactGA.initialize('UA-37733531-9');
        } else {
            ReactGA.initialize('UA-37733531-9', { testMode: true });
        }
        setReady(true);
    }, []);

    if (ready === false) return null;

    return (
        <BrowserRouter>
            <Navigation />

            <Switch>
                <Route component={Login} exact path="/login" />
                <Route component={McpAuthorize} exact path="/mcp/authorize" />
                <Route component={ForgotPassword} exact path="/forgotpassword" />
                <Route
                    component={ResetPassword}
                    exact
                    path="/resetpassword/:userId/:passwordReset"
                />

                <PrivateRoute component={HostSelect} exact path="/host_select" />
                <PrivateRoute component={HostRedirect} exact path="/host_redirect" />
                <PrivateRoute hostRequired component={Home} exact path="/" />
                <PrivateRoute hostRequired component={EventList} exact path="/events" />
                <PrivateRoute hostRequired component={Settings} exact path="/settings" />
                <PrivateRoute
                    hostRequired
                    component={StripeOnboarding}
                    exact
                    path="/settings/onboarding"
                />

                <PrivateRoute hostRequired component={EventNavigator} path="/events/:id" />
                <PrivateRoute hostRequired component={AdminNavigator} path="/admin" />

                <Route path="*" component={PageNotFound} />
            </Switch>

            <Notification />
        </BrowserRouter>
    );
}

/**
 * Renders top level navigation
 *
 * Desktop: AppBar
 * Mobile: BottomNavigation
 */

function Navigation() {
    const { auth } = useAuth();

    const eventView = useRouteMatch({ path: '/events/:id' });
    const adminView = useRouteMatch({ path: '/admin' });
    const mcpConsentView = useRouteMatch({ path: '/mcp/authorize' });
    const authenticated = Boolean(auth?.hostId);

    const navigatorVisible = Boolean(!eventView && !adminView && authenticated);
    const showAdminMenu = Boolean(auth?.admin && authenticated);

    const location = useLocation();

    // Current User/Host
    const userId = auth?.userId;
    const hostId = auth?.hostId;
    const hostName = auth?.hostName;
    const currentPage = location.pathname.replace(/\//g, '') || ''; // Regex: Remove all instances of '/' character in the pathname

    // GA Log whenever the page changes
    useEffect(() => {
        if (hostId && hostName) {
            ReactGA.set({ hostId, hostName, userId, page: currentPage || '/' });
            ReactGA.pageview(currentPage || '/');
        }
    }, [currentPage, userId, hostId, hostName]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentPage]);

    // Consent is a standalone flow; navigation must not cover its approval controls.
    if (mcpConsentView) return null;

    return (
        <>
            {navigatorVisible && (
                <>
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <RootAppBar admin={auth?.admin} hostId={auth?.hostId} />
                    </Box>

                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        <RootTabs admin={auth?.admin} hostId={auth?.hostId} />
                    </Box>
                </>
            )}
            {showAdminMenu && (
                <AdminMenu
                    hostId={auth.hostId}
                    hostName={auth.hostName}
                    navigatorVisible={navigatorVisible}
                />
            )}
        </>
    );
}
